/**
 * Engine-owned reactive cache for fetched TTS chunks — the replacement for
 * mirroring chunk data into the TanStack queryClient. The chunk fetcher writes
 * results here and `useCurrentAudioChunkQuery` reads them synchronously by
 * key, so chunk-boundary transitions render prefetched chunks without a
 * pending flash while TanStack stays entirely out of the chunk path (the old
 * mirror left a live `queryFn` behind that could race a duplicate fetch
 * outside the fetcher's single-flight).
 *
 * Bounded FIFO: sequential playback and the prefetch window only ever touch
 * the most recently inserted ~10 keys, so insertion-order eviction behaves
 * like LRU here without write-on-read bookkeeping. Evicted chunks simply
 * refetch (the old mirror's entries were garbage-collected by TanStack the
 * same way).
 *
 * Entries are `PlaybackChunk`, a discriminated union: readers narrow on
 * `playbackMode` rather than casting per call site.
 */
import { Atom, type AtomRegistry } from 'effect/reactivity';
import type { PlaybackChunk } from '../query/tts-chunks';

export const legacyChunkCacheKey = (params: {
  chapterId: string;
  ttsId: string;
  voice?: string;
}) => `legacy|${params.chapterId}|${params.ttsId}|${params.voice ?? ''}`;

export const docPackChunkCacheKey = (params: {
  audioId: string;
  docPackVersion: number;
  chunkId: string;
  voice?: string;
}) =>
  `doc-pack|${params.audioId}|${params.docPackVersion.toString()}|${params.chunkId}|${params.voice ?? ''}`;

export type AudioChunkCacheState = Readonly<Record<string, PlaybackChunk>>;

const emptyAudioChunkCacheState: AudioChunkCacheState = {};

export const AUDIO_CHUNK_CACHE_CAPACITY = 48;

export const audioChunkCacheAtom = Atom.writable<
  AudioChunkCacheState,
  AudioChunkCacheState
>(
  () => emptyAudioChunkCacheState,
  (ctx, value) => {
    ctx.setSelf(value);
  },
).pipe(Atom.keepAlive);

export const readAudioChunkCacheState = (
  state: AudioChunkCacheState,
  key: string,
): PlaybackChunk | undefined => state[key];

export const readAudioChunkCache = (
  registry: AtomRegistry.AtomRegistry,
  key: string,
): PlaybackChunk | undefined =>
  readAudioChunkCacheState(registry.get(audioChunkCacheAtom), key);

export const insertAudioChunkCacheState = (
  state: AudioChunkCacheState,
  key: string,
  chunk: PlaybackChunk,
): AudioChunkCacheState => {
  const entries = { ...state, [key]: chunk };
  // FIFO eviction rides on string-key insertion order, which the spec
  // guarantees for these non-index keys (an overwrite keeps its original
  // slot rather than moving to the back).
  const keys = Object.keys(entries);
  if (keys.length <= AUDIO_CHUNK_CACHE_CAPACITY) return entries;
  const retained: Record<string, PlaybackChunk> = {};
  for (const retainedKey of keys.slice(
    keys.length - AUDIO_CHUNK_CACHE_CAPACITY,
  )) {
    const retainedChunk = entries[retainedKey];
    if (retainedChunk !== undefined) retained[retainedKey] = retainedChunk;
  }
  return retained;
};

export const writeAudioChunkCache = (
  registry: AtomRegistry.AtomRegistry,
  key: string,
  chunk: PlaybackChunk,
): void => {
  registry.set(
    audioChunkCacheAtom,
    insertAudioChunkCacheState(registry.get(audioChunkCacheAtom), key, chunk),
  );
};

/** Drop every entry matching the predicate — e.g. chunks whose downloaded
 * content was deleted or rebuilt under the same ttsId, which the versionless
 * `legacy|` keys would otherwise keep serving stale. */
export const evictAudioChunkCacheWhere = (
  registry: AtomRegistry.AtomRegistry,
  predicate: (chunk: PlaybackChunk) => boolean,
): void => {
  const state = registry.get(audioChunkCacheAtom);
  const retained: Record<string, PlaybackChunk> = {};
  let evicted = false;
  for (const [key, chunk] of Object.entries(state)) {
    if (predicate(chunk)) evicted = true;
    else retained[key] = chunk;
  }
  if (evicted) registry.set(audioChunkCacheAtom, retained);
};
